import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { TIER_ORDER, TIERS, TERRITORIES, TERRITORY_ORDER, LIFECYCLE_OPTIONS, STATE_NAME } from "../data/broker";
import { jitterInState } from "../data/geo";

const STATE_CODES = Object.keys(STATE_NAME).sort();

const emptyForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  linkedin_url: "",
  hubspot_contact_link: "",
  company_name: "",
  company_hubspot_link: "",
  contact_owner: "",
  last_activity_date: "",
  lead_status: "",
  marketing_contact_status: "",
  territory: "",
  state: "",
  city: "",
  tier: "t5",
  lifecycle_stage: "",
  note: "",
  is_abm: true,
  tpa_name: "",
  kind: "contact",
};

export default function ContactDialog({ open, coords, initial, onCancel, onSave, allTpas = [] }) {
  const isEdit = Boolean(initial?.id);
  const isSalesTeam = initial?.kind === "sales_team";
  const isTpaContact = initial?.kind === "tpa_contact";
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        first_name: initial.first_name || "",
        last_name: initial.last_name || "",
        email: initial.email || "",
        phone: initial.phone || "",
        linkedin_url: initial.linkedin_url || "",
        hubspot_contact_link: initial.hubspot_contact_link || "",
        company_name: initial.company_name || (initial.tpa_name || ""),
        company_hubspot_link: initial.company_hubspot_link || "",
        contact_owner: initial.contact_owner || "",
        last_activity_date: initial.last_activity_date || "",
        lead_status: initial.lead_status || "",
        marketing_contact_status: initial.marketing_contact_status || "",
        territory: initial.territory || "",
        state: initial.state || "",
        city: initial.city || "",
        tier: initial.tier || (initial.kind === "tpa_contact" ? "tpa" : "t5"),
        lifecycle_stage: initial.lifecycle_stage || "",
        note: initial.note || "",
        is_abm: initial.is_abm !== false,
        tpa_name: initial.tpa_name || "",
        kind: initial.kind || "contact",
      });
    } else {
      setForm(emptyForm);
    }
    setErr("");
  }, [open, initial]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!isSalesTeam && !isTpaContact && !form.company_name.trim()) {
      setErr("Company name is required.");
      return;
    }
    if (isTpaContact && !form.tpa_name) {
      setErr("Pick a TPA.");
      return;
    }
    if (!isSalesTeam && !isTpaContact && !TIER_ORDER.includes(form.tier)) {
      setErr("Pick a tier.");
      return;
    }
    setSaving(true);
    try {
      // If no coords were provided (form-only path) but a state was picked,
      // auto-jitter within the state centroid so the contact still renders.
      let payload = { ...form };
      if (isSalesTeam) {
        // Force-preserve sales team identity + tier regardless of form state.
        payload.kind = "sales_team";
        payload.tier = "sales";
        payload.is_abm = false;
        if (!payload.company_name) payload.company_name = "Virtue Health · Sales Team";
      }
      if (isTpaContact) {
        payload.kind = "tpa_contact";
        payload.tier = "tpa";
        payload.is_abm = false;
        payload.company_name = payload.tpa_name || payload.company_name;
      }
      const alreadyHasCoords = Boolean(coords) || Boolean(initial?.coords);
      if (!alreadyHasCoords && payload.state) {
        const c = jitterInState(payload.state);
        if (c) payload.coords = c;
      }
      await onSave(payload);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? onCancel() : null)}>
      <DialogContent
        className="!bg-[#0a0a0c] !border-white/10 !text-zinc-100 !max-w-2xl max-h-[92vh] overflow-y-auto"
        data-testid="contact-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display font-black text-2xl tracking-tight text-zinc-50">
            {isSalesTeam
              ? "Edit Sales Team Member"
              : isTpaContact
              ? isEdit
                ? "Edit TPA Contact"
                : "New TPA Contact"
              : isEdit
              ? "Edit Contact"
              : "New Contact"}
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-[12px]">
            {isEdit
              ? "Update any field. Changes are saved with your editor name."
              : coords
                ? `Dropping at ${coords[0].toFixed(3)}, ${coords[1].toFixed(3)}.`
                : "Fill in the contact details."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="First Name" testid="cf-first">
              <input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className={inputCls} data-testid="cf-first" />
            </Field>
            <Field label="Last Name" testid="cf-last">
              <input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className={inputCls} data-testid="cf-last" />
            </Field>
          </div>

          <Field
            label={isSalesTeam ? "Team / Company" : isTpaContact ? "TPA *" : "Associated Company *"}
            testid="cf-company"
          >
            {isTpaContact ? (
              <select
                value={form.tpa_name}
                onChange={(e) => {
                  set("tpa_name", e.target.value);
                  set("company_name", e.target.value);
                }}
                className={inputCls}
                data-testid="cf-tpa-name"
                required
              >
                <option value="">— pick a TPA —</option>
                {allTpas.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            ) : (
              <input
                value={form.company_name}
                onChange={(e) => set("company_name", e.target.value)}
                className={inputCls}
                data-testid="cf-company"
                required={!isSalesTeam}
                placeholder={isSalesTeam ? "Virtue Health · Sales Team" : undefined}
              />
            )}
          </Field>

          {isTpaContact && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Title (optional)" testid="cf-title">
                <input
                  value={form.lifecycle_stage}
                  onChange={(e) => set("lifecycle_stage", e.target.value)}
                  className={inputCls}
                  placeholder="Account Manager"
                  data-testid="cf-title"
                />
              </Field>
              <Field label="State (optional — for map pin)" testid="cf-tpa-state">
                <select
                  value={form.state}
                  onChange={(e) => set("state", e.target.value)}
                  className={inputCls}
                  data-testid="cf-tpa-state"
                >
                  <option value="">—</option>
                  {STATE_CODES.map((s) => (
                    <option key={s} value={s}>{s} · {STATE_NAME[s]}</option>
                  ))}
                </select>
              </Field>
            </div>
          )}

          {!isSalesTeam && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Email" testid="cf-email">
                <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="cf-email" />
              </Field>
              <Field label="Phone" testid="cf-phone">
                <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="cf-phone" />
              </Field>
            </div>
          )}

          <Field label="LinkedIn URL" testid="cf-linkedin">
            <input value={form.linkedin_url} onChange={(e) => set("linkedin_url", e.target.value)} className={inputCls} placeholder="https://www.linkedin.com/in/…" data-testid="cf-linkedin" />
          </Field>

          <div className={(isSalesTeam || isTpaContact) ? "" : "grid grid-cols-2 gap-3"}>
            {!isTpaContact && (
              <Field label="HubSpot Contact Link" testid="cf-hubcontact">
                <input value={form.hubspot_contact_link} onChange={(e) => set("hubspot_contact_link", e.target.value)} className={inputCls} data-testid="cf-hubcontact" />
              </Field>
            )}
            {!isSalesTeam && !isTpaContact && (
              <Field label="HubSpot Company Link" testid="cf-hubcompany">
                <input value={form.company_hubspot_link} onChange={(e) => set("company_hubspot_link", e.target.value)} className={inputCls} data-testid="cf-hubcompany" />
              </Field>
            )}
          </div>

          {!isSalesTeam && !isTpaContact && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Contact Owner" testid="cf-owner">
                  <input value={form.contact_owner} onChange={(e) => set("contact_owner", e.target.value)} className={inputCls} data-testid="cf-owner" />
                </Field>
                <Field label="Last Activity Date" testid="cf-lastact">
                  <input value={form.last_activity_date} onChange={(e) => set("last_activity_date", e.target.value)} className={inputCls} placeholder="MM/DD/YYYY" data-testid="cf-lastact" />
                </Field>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <Field label="Territory" testid="cf-terr">
                  <select value={form.territory} onChange={(e) => set("territory", e.target.value)} className={inputCls} data-testid="cf-terr">
                    <option value="">—</option>
                    {TERRITORY_ORDER.map((k) => (
                      <option key={k} value={k}>{TERRITORIES[k].label}</option>
                    ))}
                  </select>
                </Field>
                <Field label="State" testid="cf-state">
                  <select value={form.state} onChange={(e) => set("state", e.target.value)} className={inputCls} data-testid="cf-state">
                    <option value="">—</option>
                    {STATE_CODES.map((s) => (
                      <option key={s} value={s}>{s} · {STATE_NAME[s]}</option>
                    ))}
                  </select>
                </Field>
                <Field label="City (optional)" testid="cf-city">
                  <input value={form.city} onChange={(e) => set("city", e.target.value)} className={inputCls} data-testid="cf-city" />
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Lifecycle Stage" testid="cf-lc">
                  <select
                    value={form.lifecycle_stage}
                    onChange={(e) => {
                      const v = e.target.value;
                      set("lifecycle_stage", v);
                      const opt = LIFECYCLE_OPTIONS.find((o) => o.value === v);
                      if (opt) set("tier", opt.tier);
                    }}
                    className={inputCls}
                    data-testid="cf-lc"
                  >
                    {LIFECYCLE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.value || "— (No Traction)"}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Tier" testid="cf-tier">
                  <select value={form.tier} onChange={(e) => set("tier", e.target.value)} className={inputCls} data-testid="cf-tier">
                    {TIER_ORDER.map((tk) => (
                      <option key={tk} value={tk}>
                        {TIERS[tk].short} · {TIERS[tk].label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            </>
          )}

          {isSalesTeam && (
            <div className="border border-[#B85450]/40 bg-[#B85450]/5 px-3 py-2 font-mono text-[10px] tracking-[0.16em] uppercase text-[#D97570]">
              Sales Team member — CRM fields hidden. Tier stays locked to VH · Sales Team.
            </div>
          )}

          {isTpaContact && (
            <div className="border border-indigo-400/40 bg-indigo-500/5 px-3 py-2 font-mono text-[10px] tracking-[0.16em] uppercase text-indigo-200">
              TPA Contact — CRM tier/territory/HubSpot fields hidden. Never counted toward ABM or quote analytics.
            </div>
          )}

          {!isSalesTeam && !isTpaContact && (
            <label className="flex items-center gap-2 font-mono text-[11px] text-zinc-400 uppercase tracking-[0.15em]">
              <input
                type="checkbox"
                checked={form.is_abm}
                onChange={(e) => set("is_abm", e.target.checked)}
                data-testid="cf-abm"
              />
              ABM · Target Account List
            </label>
          )}

          <Field label="Notes" testid="cf-note">
            <textarea value={form.note} onChange={(e) => set("note", e.target.value)} rows={2} className={inputCls} data-testid="cf-note" />
          </Field>

          {err && (
            <div className="border border-rose-500/50 bg-rose-500/10 text-rose-200 px-3 py-2 font-mono text-[11px] tracking-[0.16em] uppercase">
              {err}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-white/10 hover:border-white/30 text-zinc-300 font-mono text-[11px] uppercase tracking-[0.16em]"
              data-testid="contact-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 border border-cyan-400/60 bg-cyan-400/10 hover:bg-cyan-400/20 text-cyan-100 font-mono text-[11px] uppercase tracking-[0.16em] disabled:opacity-50"
              data-testid="contact-save"
            >
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Contact"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const inputCls =
  "w-full bg-[#0d0d10] border border-white/10 focus:border-cyan-400/50 outline-none px-3 py-2 font-mono text-[12px] text-zinc-100 placeholder:text-zinc-600";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block font-mono text-[9px] tracking-[0.22em] uppercase text-zinc-500 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
